<template>
    <div
        ref="colorWidget"
        :class="[
            buttonState(editor.can().setColor() && editorSelection.text.length > 0),
            'relative inline-block flex-none text-sm',
        ]"
    >
        <button
            ref="colorButton"
            v-tippy="t('templateTextColor')"
            :class="[
                buttonState(editor.can().toggleHighlight() && editorSelection.text.length > 0),
                'flex p-1',
            ]"
            :disabled="!editor.can().toggleHighlight() || editorSelection.text.length === 0"
            @click="togglePicker()"
        >
            <MdiFormatColorFill
                class="h-6 w-6"
                :style="`border-bottom: 2px solid ${pureColor}`"
            />
            <MdiChevronDown class="h-6 w-4" />
        </button>
        <div
            v-if="colorBgOpen"
            ref="pickerEl"
            class="fixed top-10 left-0 z-[2002] mt-2 flex flex-col rounded-md bg-white shadow-lg"
            :style="{
                top: pickerPosition.top + 'px',
                left: pickerPosition.left + 'px',
            }"
        >
            <color-picker
                v-model:pure-color="pureColor"
                v-model:gradient-color="defaultGradient"
                picker-type="fk"
                use-type="pure"
                shape="square"
                lang="en"
                :is-widget="true"
                :disable-alpha="true"
            />
            <ActionButton
                :value="t('buttonUse')"
                state="info"
                :icon="MdiCheck"
                class-override="rounded-t-none"
                @click="changeBgColor(editor)"
            />
        </div>
    </div>
</template>

<script setup>
    import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
    import { buttonState } from '../../composables/useWYSIWYGEditorUtils';
    import useClickOutside from '../../composables/useClickOutside';
    import { MdiChevronDown, MdiFormatColorFill, MdiCheck } from 'materialdesignicons-vue3/icons/';
    import ActionButton from '../../ui/ActionButton.vue';
    import { getDefaultBgColor } from '../../composables/useColor';
    import { ColorPicker } from 'vue3-colorpicker';
    import 'vue3-colorpicker/style.css';

    defineExpose({
        name: 'TextColorPicker',
    });

    const props = defineProps({
        editor: {
            type: Object,
            required: true,
        },
        editorSelection: {
            type: Object,
            required: true,
        },
    });

    // Nastavení nové barvy
    const defaultGradient = ref(
        'linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)',
    );

    const colorButton = ref(null);
    const pureColor = ref(getDefaultBgColor());
    const colorBgOpen = ref(false);
    const colorWidget = ref(null);
    const pickerPosition = ref({ top: 0, left: 0 });
    const editor = props.editor;
    const editorSelection = props.editorSelection;
    const pickerEl = ref(null);

    const changeBgColor = (editor) => {
        editor.chain().focus().toggleHighlight({ color: pureColor.value }).run();
        colorBgOpen.value = false;
    };

    // Získání aktuální barvy
    const extractBgColor = () => {
        const colorCode = editor.getAttributes('highlight').color || 'rgb(255, 255, 255)';
        pureColor.value = colorCode;
        return colorCode;
    };

    // Otevření/zavření pickeru s výpočtem pozice
    const togglePicker = () => {
        colorBgOpen.value = !colorBgOpen.value;
        if (colorBgOpen.value) {
            handleResize();
        }
    };

    const handleResize = async () => {
        if (!colorButton.value) return;

        // počkej až se picker opravdu vykreslí, ať ho můžeme změřit
        await nextTick();
        await new Promise((r) => requestAnimationFrame(r));

        const buttonRect = colorButton.value.getBoundingClientRect();
        const gap = 1;

        // ber panel co nejblíž tlačítku (bezpečnější než globální querySelector)
        const panelEl =
            colorButton.value.closest('.editor-wrapper') ??
            document.querySelector('.editor-wrapper');
        const panelRect = panelEl?.getBoundingClientRect();

        const pickerRect = pickerEl.value?.getBoundingClientRect();
        const pickerWidth = pickerRect?.width ?? 280;

        const leftLimit = (panelRect?.left ?? 0) + gap;
        const rightLimit = (panelRect?.right ?? window.innerWidth) - gap;

        let left = buttonRect.left;
        const top = buttonRect.bottom + gap;

        if (left + pickerWidth > rightLimit) left = rightLimit - pickerWidth;
        if (left < leftLimit) left = leftLimit;

        pickerPosition.value = { top, left };
    };

    useClickOutside(colorWidget, () => {
        colorBgOpen.value = false;
    });

    onMounted(() => {
        window.addEventListener('resize', handleResize);
        extractBgColor();
    });

    onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
        extractBgColor();
    });

    watch(
        () => editorSelection.from,
        () => {
            extractBgColor();
        },
    );

    watch(
        () => editorSelection.text,
        () => {
            extractBgColor();
        },
    );
</script>
